import CheckIcon from "@mui/icons-material/Check";
import LanguageIcon from "@mui/icons-material/Language";
import {
    FilledInput,
    FormControl,
    FormHelperText,
    InputLabel,
    Link,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Select,
    SelectChangeEvent,
    SelectProps,
} from "@mui/material";
import React, { MouseEvent, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { getEnabledLocaleConfigs, EnabledAppLocale } from "./localeRegistry";
import { asEnabledAppLocale, persistAppLocale } from "./localeResolver";
import { changeAppLanguage } from "./i18n";

interface LanguageSelectProps {
    fullWidth?: boolean;
    showHelperText?: boolean;
    size?: SelectProps["size"];
}

const useLanguageSelection = () => {
    const { t, i18n } = useTranslation<"common">("common");
    const locale = asEnabledAppLocale(i18n.resolvedLanguage ?? i18n.language) ?? "en";

    const changeLocale = async (value: string) => {
        const nextLocale = asEnabledAppLocale(value);
        if (!nextLocale) {
            return;
        }
        await changeAppLanguage(i18n, nextLocale);
        persistAppLocale(nextLocale);
    };

    return { changeLocale, locale, t };
};

export const LanguageSelect: React.FC<LanguageSelectProps> = ({ fullWidth = true, showHelperText = false, size = "small" }) => {
    const { changeLocale, locale, t } = useLanguageSelection();
    const id = useId();

    const handleChange = (event: SelectChangeEvent<EnabledAppLocale>) => {
        void changeLocale(event.target.value);
    };

    return (
        <FormControl fullWidth={fullWidth} size={size} variant={"filled"}>
            <InputLabel id={`${id}-label`} variant={"filled"}>
                {t("common:language.label")}
            </InputLabel>
            <Select
                labelId={`${id}-label`}
                id={id}
                value={locale}
                label={t("common:language.label")}
                onChange={handleChange}
                variant={"filled"}
                input={<FilledInput />}
            >
                {getEnabledLocaleConfigs().map((config) => (
                    <MenuItem key={config.tag} value={config.tag}>
                        {config.nativeName}
                    </MenuItem>
                ))}
            </Select>
            {showHelperText && <FormHelperText>{t("common:language.description")}</FormHelperText>}
        </FormControl>
    );
};

export const LanguageMenu: React.FC = () => {
    const { changeLocale, locale, t } = useLanguageSelection();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const id = useId();
    const open = Boolean(anchorEl);
    const currentLocale = getEnabledLocaleConfigs().find((config) => config.tag === locale);

    const openMenu = (event: MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const closeMenu = () => {
        setAnchorEl(null);
    };

    const selectLocale = (nextLocale: string) => {
        closeMenu();
        void changeLocale(nextLocale);
    };

    return (
        <>
            <Link
                component={"button"}
                variant={"caption"}
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
                aria-controls={open ? `${id}-menu` : undefined}
                aria-expanded={open ? "true" : undefined}
                aria-haspopup={"menu"}
                aria-label={t("common:language.label")}
                id={`${id}-button`}
                onClick={openMenu}
            >
                <LanguageIcon fontSize={"inherit"} />
                {currentLocale?.nativeName}
            </Link>
            <Menu id={`${id}-menu`} anchorEl={anchorEl} open={open} onClose={closeMenu} MenuListProps={{ "aria-labelledby": `${id}-button` }}>
                {getEnabledLocaleConfigs().map((config) => (
                    <MenuItem key={config.tag} selected={config.tag === locale} onClick={() => selectLocale(config.tag)}>
                        <ListItemIcon>{config.tag === locale && <CheckIcon fontSize={"small"} />}</ListItemIcon>
                        <ListItemText>{config.nativeName}</ListItemText>
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};
